import type { Observable } from 'rxjs'
import { catchError, forkJoin, map, of, switchMap } from 'rxjs'
import { effect, Injectable, inject, untracked } from '@angular/core'
import { apiEndpoint, app } from '@awork/environments/environment'
import type { UserRole } from '@awork/features/workspace/models/role.model'
import { Role } from '@awork/features/workspace/models/role.model'
import type { QueryParams } from '@awork/_shared/services/api-client/ApiClient'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import {
  AccessLevels,
  Feature,
  Features,
  PagePermissionId,
  Permissions,
  TAG_MANAGEMENT_REQUIRED_PERMISSION,
  UserPermissions
} from '@awork/features/workspace/models/permissions.model'
import type { RequiredWorkspacePermission } from '@awork/features/workspace/models/permissions.model'
import { Router } from '@angular/router'
import type { Project } from '@awork/features/project/models/project.model'
import type { ProjectRole } from '@awork/features/project/models/project-role.model'
import { TitleCasePipe } from '@angular/common'
import { PermissionsStore } from '@awork/features/workspace/state/permissions.store'
import { PermissionsQuery } from '@awork/features/workspace/state/permissions.query'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { AppQuery } from '@awork/core/state/app.query'
import { UserQuery } from '@awork/features/user/state/user.query'
import type { HttpParams } from '@angular/common/http'
import type { WorkspaceFeature } from '@awork/features/workspace/models/workspace.model'
import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'
import { TeamQuery } from '@awork/features/team/state/team.query'
import type { IDeleteRoleBody } from '@awork/features/workspace/services/permission-service/types'
import { Plan } from '@awork/_shared/models/subscription.types'
import { FeatureId } from '@awork/_shared/models/subscription-plan.model'
import { SubscriptionPlanQuery } from '@awork/_shared/state/subscription-plan.query'
import { checkPermissions, checkProjectRolePermissions, hasProjectOwnTeamPermissions } from './permissions.helpers'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import type { UserRoleMapping } from '@awork/features/user/state/types'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { ProjectBudgetAccessChangeService } from '@awork/features/workspace/services/project-budget-access-change-service/project-budget-access-change.service'

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private apiClient = inject(ApiClient)
  private permissionsStore = inject(PermissionsStore)
  private permissionsQuery = inject(PermissionsQuery)
  private router = inject(Router)
  private appQuery = inject(AppQuery)
  private userQuery = inject(UserQuery)
  private subscriptionQuery = inject(SubscriptionQuery)
  private workspaceQuery = inject(WorkspaceQuery)
  private teamQuery = inject(TeamQuery)
  private subscriptionPlanQuery = inject(SubscriptionPlanQuery)
  private featureFlagQuery = inject(FeatureFlagQuery)
  private projectQuery = inject(ProjectQuery)
  private readonly projectBudgetAccessChangeService = inject(ProjectBudgetAccessChangeService)

  private readonly projects = this.projectQuery.queryAll()

  private permissionsRequestVersion = 0
  private url: string
  private roles: Role[] = []

  permissionsFetched: boolean

  /** Initializes permission loading and observes changes to budget access. */
  constructor() {
    this.url = `${apiEndpoint}/`
    this.permissionsFetched = false

    // To fetch the permissions again after logout and login
    effect(() => {
      const isLoggedIn = this.appQuery.query('isLoggedIn')

      if (!isLoggedIn()) {
        this.permissionsFetched = false
      }
    })

    effect(() => this.refreshBudgetAccess())
  }

  /**
   * Clears stale budget data when signed permissions or effective project access change.
   * @withSignal {Signal<Project[]>} projects - Loaded projects and their current memberships.
   */
  private refreshBudgetAccess(): void {
    const permissions = this.permissionsQuery.getUserPermissions()
    const projects: Record<string, boolean> = {}

    if (permissions?.bpe === true) {
      this.projects().forEach(project => {
        projects[project.id] = this.canReadProjectBudget(project.id)
      })
    }

    untracked(() => this.projectBudgetAccessChangeService.handlePermissionsLoaded(permissions, projects))
  }

  /**
   * Determines if the current user is allowed to make a certain action.
   * This method checks for admin permissions only.
   * @param {boolean} [redirect=false]
   * @returns {boolean}
   */
  public isAllowedAdmin(redirect = false): boolean {
    const userPermissions = this.permissionsQuery.getUserPermissions()

    if (userPermissions) {
      if (userPermissions.isAdmin) {
        return true
      } else {
        return this.notAllowedAction(redirect)
      }
    }

    return false
  }

  /**
   * Checks whether the signed permissions enforce Project times protection of budgets and planned effort.
   * @returns {boolean} True only when the API sent bpe as true.
   */
  isBudgetProtectionEnabled(): boolean {
    return this.permissionsQuery.getUserPermissions()?.bpe === true
  }

  /**
   * Checks Project times Read for budget data on a source project. Unloaded projects are denied.
   * Tasks without a project keep their current behavior.
   * @param {string | null | undefined} projectId - Source project id.
   * @returns {boolean} True when budget data may be shown or edited.
   */
  canReadProjectBudget(projectId?: string | null): boolean {
    return projectId ? this.isAllowed(Features.ProjectBudget, AccessLevels.Read, false, projectId) : true
  }

  /**
   * Determines if the current user is allowed to use a feature
   * @param {Features | Features[]} feature - The requested feature.
   * @param {AccessLevels} accessLevel - The required access level.
   * @param {boolean} [redirect=false] - True to open the access-denied page.
   * @param {string} projectId - Project id for the project role check.
   * @param {boolean} [forceProjectCheck=false] - True to enforce project roles when project data is unavailable.
   * @returns {boolean} True if is allowed, false otherwise
   */
  // eslint-disable-next-line complexity
  isAllowed(
    feature: Features | Features[],
    accessLevel: AccessLevels,
    redirect = false,
    projectId?: string,
    forceProjectCheck = false
  ): boolean {
    if (feature === Features.ProjectBudget) {
      if (!this.isBudgetProtectionEnabled()) {
        return true
      }

      // Unloaded projects are unknown. Hide edit controls until they load.
      if (projectId && !this.getProjectById(projectId)) {
        return this.notAllowedAction(redirect)
      }

      return this.isAllowed(Features.ProjectTimetracking, AccessLevels.Read, redirect, projectId)
    }

    const userPermissions = this.permissionsQuery.getUserPermissions()
    const project = this.getProjectById(projectId)

    let features: Features[]

    if (!(feature instanceof Array)) {
      features = [feature]
    } else {
      features = feature
    }

    if (!userPermissions) {
      this.notAllowedAction(redirect)
    }

    if (project?.isExternal || this.isPrivateProjectNotOwnedByUser(project)) {
      return checkProjectRolePermissions(userPermissions, features, accessLevel, redirect, project, () =>
        this.notAllowedAction(redirect)
      )
    }

    // For the case forceProjectCheck is true but the project is not available, disallow the user
    if (forceProjectCheck && !project) {
      return false
    }

    // If is an admin or 'None' is required or no features were specified, is allowed
    if (
      userPermissions.isAdmin ||
      accessLevel === undefined ||
      accessLevel === null ||
      accessLevel === AccessLevels.None ||
      !features ||
      features.length === 0
    ) {
      return true
    }

    // Check global permissions
    if (userPermissions.permissions?.length) {
      // If the user has the needed global permission, allow it,
      // otherwise proceed to the project role permissions check (if applies)
      if (
        checkPermissions(userPermissions.permissions, features, accessLevel, redirect, () =>
          this.notAllowedAction(redirect)
        )
      ) {
        return true
      }
    }

    // Check for project ownership
    if (projectId) {
      const currentUser = this.userQuery.getCurrentUser()

      if (currentUser && project?.createdBy === currentUser.id) {
        return true
      }

      return checkProjectRolePermissions(userPermissions, features, accessLevel, redirect, project, () =>
        this.notAllowedAction(redirect)
      )
    }

    return this.notAllowedAction(redirect)
  }

  /**
   * Evaluates optional required-permission metadata declared by routes and navigation entries.
   * @param {RequiredWorkspacePermission} requirement The optional required permission.
   * @returns {boolean} True when no requirement exists or the current user satisfies it
   */
  hasRequiredPermission(requirement?: RequiredWorkspacePermission): boolean {
    if (!requirement || !this.isAllowed(requirement.feature, requirement.access)) {
      return !requirement
    }

    return requirement.additionalPermissions?.every(permission => this.hasRequiredPermission(permission)) ?? true
  }

  /**
   * Gets the current effective workspace and project-role permissions.
   * @returns {UserPermissions | null} The loaded permission model, or null before it is available.
   */
  getCurrentUserPermissions(): UserPermissions | null {
    return this.permissionsQuery.getUserPermissions() ?? null
  }

  /**
   * Determines if the current user has Own Team permissions for a given project
   * It is important to use this check in combination with isAllowed of global project features (Master/Planning)
   * @param {string} projectId The project id to check
   * @returns {boolean} True if is allowed, false otherwise
   */
  isAllowProjectOwnTeamPermissions(projectId: string): boolean {
    const userPermissions = this.permissionsQuery.getUserPermissions()
    const project = this.getProjectById(projectId)

    if (userPermissions.isAdmin) {
      return true
    }

    const hasOwnTeamsPermission = userPermissions.permissions.find(
      permission => permission.feature === Feature.getFeatureStringByEnum(Features.ProjectOwnTeam)
    )

    if (!hasOwnTeamsPermission) {
      return true
    }

    if (!project) {
      return false
    }

    const ownTeams = this.teamQuery.getUserTeams(userPermissions.userId)

    return hasProjectOwnTeamPermissions(project, ownTeams, userPermissions)
  }

  /**
   * Checks if the user is allowed to see the specific menuitem
   * @param {string} menuItemId
   * @param {boolean} redirect
   * @returns {boolean}
   */
  public isAllowedToSeeMenuItem(menuItemId: string, redirect: boolean): boolean {
    const userPermissions = this.permissionsQuery.getUserPermissions()

    const allowedToSee = !userPermissions.deactivatedMenuItems.includes(menuItemId)

    if (!allowedToSee) {
      return this.notAllowedAction(redirect)
    } else {
      return true
    }
  }

  /**
   * Executes the action required when the user is not allowed for certain feature
   * @param redirect True to redirect to 'not allowed' page
   * @returns {boolean}
   */
  private notAllowedAction(redirect): boolean {
    if (redirect && app === 'web') {
      this.router.navigate(['/not-allowed'], { skipLocationChange: true })
    }

    return false
  }

  /**
   * Checks if a project is private and not owned by the current user
   * @param {Project} project {Project} The project to check
   * @returns {boolean} True if project is private and not owned by current user
   */
  private isPrivateProjectNotOwnedByUser(project?: Project): boolean {
    if (!project?.isPrivate) {
      return false
    }

    const currentUser = this.userQuery.getCurrentUser()

    return currentUser ? project.createdBy !== currentUser.id : false
  }

  /**
   * Gets the project from the project query by id
   * @param {string} projectId The project id to resolve
   * @returns {Project | undefined} The resolved project when available
   */
  private getProjectById(projectId?: string): Project | undefined {
    if (!projectId) {
      return undefined
    }

    const project = this.projectQuery.queryProject(projectId)

    return project()
  }

  /**
   * Makes an API call to request the user's permissions
   * @returns {Observable<UserPermissions>} Permissions after budget cleanup, or null if loading or cleanup fails.
   */
  public fetchPermissions(): Observable<UserPermissions> {
    this.permissionsFetched = true

    const requestVersion = ++this.permissionsRequestVersion

    return this.apiClient.get<UserPermissions>(`${this.url}me/permissions`).pipe(
      map(permissions => {
        if (requestVersion !== this.permissionsRequestVersion) {
          return this.permissionsQuery.getUserPermissions()
        }

        const mapped = new UserPermissions(permissions.userPermission)

        this.permissionsStore.update({ ...permissions.userPermission, bpe: mapped.bpe })

        return mapped
      }),
      switchMap(permissions =>
        this.projectBudgetAccessChangeService.handlePermissionsLoaded(permissions).pipe(
          map(isReset => {
            if (!isReset) {
              this.permissionsFetched = false
            }

            return isReset ? permissions : null
          })
        )
      ),
      catchError(() => {
        this.permissionsFetched = false
        return of(null)
      })
    )
  }

  /**
   * Checks whether current workspace has any guest roles
   * @returns {Observable<boolean>}
   */
  public hasGuestRole(): Observable<boolean> {
    return this.getRoles().pipe(
      map(roles =>
        roles.some(role => {
          const mappedRole = new Role(role)

          return mappedRole.isGuestRole
        })
      )
    )
  }

  /**
   * Gets the roles of the current workspace
   * @returns {Observable<Role[]>}
   */
  public getRoles(): Observable<Role[]> {
    return this.apiClient.getAll<Role[]>(`${this.url}roles`).pipe(
      map(roles => {
        this.roles = roles.map(role => new Role(role))
        this.permissionsStore.update({ roles: this.roles })

        return this.roles
      })
    )
  }

  public getRole(nameOrId: string): Observable<Role> {
    return this.apiClient.get<Role>(`${this.url}roles/${nameOrId}`).pipe(map(role => new Role(role)))
  }

  /**
   * Gets all the user ids with their role id
   * @returns {Observable<UserRole[]>} users mapped to their specific role id
   */
  public getUsersRoles(): Observable<UserRole[]> {
    return this.apiClient.getAll<UserRole[]>(`${this.url}roles/users`)
  }

  /**
   * Gets a role by user id
   * @param {string} userId The id of the user
   * @returns {Observable<Role>} The role of the provided user
   */
  public getRoleByUserId(userId: string): Observable<Role> {
    return this.apiClient.get<Role>(`${this.url}roles/byuserid/${userId}`).pipe(map(role => new Role(role)))
  }

  /**
   * Gets the default role for inviting human users (first non admin/guest/agent role, falling back to the admin role).
   * The agent role is excluded because it is reserved for AI agent users and cannot be assigned to humans.
   * @returns {Role | undefined}
   */
  public getDefaultRole(): Role | undefined {
    let defaultRole: Role

    if (this.roles) {
      defaultRole = this.roles.find(role => !role.isAdminRole && !role.isGuestRole && !role.isAgentRole)

      if (!defaultRole) {
        defaultRole = this.roles.find(role => role.isAdminRole)
      }
    }

    return defaultRole
  }

  /**
   * Gets the admin role of the workspace
   * @returns {Role}
   */
  public getAdminRole(): Role {
    let defaultRole: Role

    if (this.roles) {
      defaultRole = this.roles.find(role => role.isAdminRole)
    }

    return defaultRole
  }

  /**
   * Post/Put the role to the API
   * @param {Role} role The role to be sent to the backend
   * @returns {Observable<Role>}
   */
  public sendRole(role: Role): Observable<Role> {
    if (role.id) {
      return this.apiClient.put<Role>(`${this.url}roles/${role.id}`, role)
    } else {
      return this.apiClient.post<Role>(`${this.url}roles`, role)
    }
  }

  /**
   * Gets the role's permissions
   * @param {string} roleId
   * @returns {Observable<Permissions[]>}
   */
  public getRolePermissions(roleId: string): Observable<Permissions[]> {
    return this.apiClient.get<Permissions[]>(`${this.url}roles/${roleId}/permissions`)
  }

  /**
   * Post/Put/Delete the role's permissions to the API
   * @param {Role} role
   * @param {Permissions} permission
   * @returns {Observable<Permissions>}
   */
  public sendRolePermission(role: Role, permission: Permissions): Observable<Permissions> {
    if (permission.accessLevels.includes(AccessLevels.None)) {
      permission.accessLevels = []
    }

    return this.apiClient.post<Permissions>(`${this.url}roles/${role.id}/permissions`, permission)
  }

  /**
   * Post/Put/Delete the role's permissions to the API
   * @param {Role} role
   * @param {Permissions[]} permissions
   * @returns {Observable<Permissions[]>}
   */
  public sendRolePermissions(role: Role, permissions: Permissions[]): Observable<Permissions[]> {
    const permissionCalls = permissions.map(p => this.sendRolePermission(role, p))
    return forkJoin(permissionCalls)
  }

  /**
   * Gets the project role's permissions
   * @param {string} roleId
   * @returns {Observable<Permissions[]>}
   */
  public getProjectRolePermissions(roleId: string): Observable<Permissions[]> {
    return this.apiClient.get<Permissions[]>(`${this.url}projectroles/${roleId}/permissions`)
  }

  /**
   * Post/Put/Delete the project role's permissions to the API
   * @param {ProjectRole} projectRole
   * @param {Permissions} permission
   * @returns {Observable<Permissions>}
   */
  public sendProjectRolePermission(projectRole: ProjectRole, permission: Permissions): Observable<Permissions> {
    const permissionToSend = { ...permission }

    if (permission.accessLevels.includes(AccessLevels.None)) {
      permissionToSend.accessLevels = []
    }

    return this.apiClient.post<Permissions>(`${this.url}projectroles/${projectRole.id}/permissions`, permissionToSend)
  }

  /**
   * Gets the role's page permissions
   * @param {string} roleId
   * @returns {Observable<string[]>}
   */
  public getRolePagePermissions(roleId: string): Observable<string[]> {
    return this.apiClient.get<string[]>(`${this.url}roles/${roleId}/deactivatedmenuitems`)
  }

  /**
   * Put the role's page permissions to the API
   * @param {Role} role
   * @param {string[]} pagePermissions
   * @returns {Observable<string>}
   */
  public sendRolePagePermission(role: Role, pagePermissions: string[]): Observable<string> {
    return this.apiClient.post<string>(`${this.url}roles/${role.id}/deactivatedmenuitems`, pagePermissions)
  }

  /**
   * Method to obatin the users specific to a roleId
   * @param {Role} role The role
   * @param {QueryParams} [queryParams={}]
   * @returns {Observable<UserRoleMapping[]>} The list of users specific to that role
   */
  getUsersSpecificToRole(role: Role, queryParams: QueryParams = {}): Observable<UserRoleMapping[]> {
    const queryParamsWithDefaults = this.setDefaultQueryParams(queryParams)

    const params: HttpParams = ApiClient.getQueryParams(queryParamsWithDefaults)

    return this.apiClient
      .getAll<{ isDeactivated: boolean; userId: string }[]>(`${this.url}roles/${role.id}/users`, { params })
      .pipe(
        map(userRole => {
          return userRole.map(user => ({
            isDeactivated: user.isDeactivated,
            userId: user.userId,
            roleId: role.id,
            name: role.name
          }))
        })
      )
  }

  /**
   * API call to move user to one role to another role
   * @param {string} userId The id of the userId
   * @param {string} oldRoleId The old role id
   * @param {string} newRoleId The new role id
   * @returns {Observable<string>}
   */
  public moveUser(userId: string, oldRoleId: string, newRoleId: string): Observable<string> {
    return this.apiClient.post<string>(`${this.url}roles/moveuser`, {
      userId,
      fromRoleId: oldRoleId,
      toRoleId: newRoleId
    })
  }

  /**
   * Makes an API call to delete the role and migrate the users, if any to a different role
   * @param {string} roleId Id of the role to be deleted
   * @param {IDeleteRoleBody} body
   * @returns {Observable<string>}
   */
  public deleteRole(roleId: string, body: IDeleteRoleBody): Observable<string> {
    return this.apiClient.post(`${this.url}roles/${roleId}/delete`, body)
  }

  /**
   * Makes an API call to get all the existing features
   * @returns {Observable<Feature[]>}
   */
  public getFeatures(): Observable<Feature[]> {
    return this.apiClient.get<string[]>(`${this.url}permissions/features`).pipe(
      map(features => {
        let featureCount = Feature.features.length + 1

        return features.map(feature => {
          const descriptiveFeature = Feature.features.find(f => f.value === feature)

          return descriptiveFeature
            ? descriptiveFeature
            : {
                id: ++featureCount,
                value: feature,
                name: new TitleCasePipe().transform(feature.replace(/-/g, ' ')),
                description: ''
              }
        })
      })
    )
  }

  /**
   * Filters the items according to the user permissions
   * @param {string} pageId
   * @param {WorkspaceFeature | FeatureId} feature
   * @returns {boolean}
   * @private
   */
  // eslint-disable-next-line complexity
  isPageVisible(pageId: string, feature?: WorkspaceFeature | FeatureId): boolean {
    let isPageVisible: boolean

    const userPermissions = this.permissionsQuery.getUserPermissions()
    const userRolePermissions = userPermissions?.permissions?.map(p => new Permissions(p)) || []
    const isAdmin = userPermissions?.isAdmin

    const isInMSTeams = this.appQuery.getIsInMSTeams()
    const isWorkspaceManager = this.isAllowed(Features.WorkspaceManager, AccessLevels.Manage)
    const isTagManagementEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.fwkTagManagement)

    const currentSubscription = this.subscriptionQuery.getSubscription()
    const currentWorkspace = this.workspaceQuery.getCurrentWorkspace()

    if (currentWorkspace && !currentWorkspace.isFeatureEnabled(feature as WorkspaceFeature)) {
      return false
    }

    if (feature && Object.values(FeatureId).includes(feature as FeatureId)) {
      const isPlanFeatureAvailable = this.subscriptionPlanQuery.queryIsPlanFeatureAvailable(feature as FeatureId)

      if (!isPlanFeatureAvailable()) {
        return false
      }
    }

    if (pageId === 'settings-tag-management') {
      return isTagManagementEnabled() && this.hasRequiredPermission(TAG_MANAGEMENT_REQUIRED_PERMISSION)
    }

    if (userPermissions.isAdmin) {
      return true
    }

    if (userPermissions?.deactivatedMenuItems) {
      const isPageEnabled = !PermissionsService.isPagePermissionDisabled(pageId, userRolePermissions)

      isPageVisible = userPermissions.isAllowedToSeeMenuItem(pageId) && isPageEnabled
    }

    if (isInMSTeams && pageId === 'settings-integrations') {
      isPageVisible = false
    }

    if (
      !isWorkspaceManager &&
      ['settings-integrations', 'settings-workspace', 'settings-teams', 'settings-forms'].includes(pageId)
    ) {
      isPageVisible = false
    }

    const isImportersEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.importers)

    if (!isWorkspaceManager && ['settings-import'].includes(pageId) && isImportersEnabled()) {
      isPageVisible = false
    }

    if (
      !isAdmin &&
      ['settings-subscription', 'settings-invite-users', 'settings-workspace-permissions', 'settings-connect'].includes(
        pageId
      )
    ) {
      isPageVisible = false
    }

    const isPlan = currentSubscription?.isPlan(Plan.Internal)

    if (isAdmin && pageId === 'settings-subscription' && isPlan) {
      isPageVisible = false
    }

    if (pageId === 'settings-categories' && this.shouldHideCategories(isPageVisible, userPermissions)) {
      isPageVisible = false
    }

    if (pageId === 'team-planner' && !this.hasUserPlannerPermissions(userPermissions)) {
      isPageVisible = false
    }

    if (pageId === 'settings-templates' && userPermissions.isGuest) {
      isPageVisible = false
    }

    return isPageVisible
  }

  /**
   * Checks if the page permission (menu item visibility) should be disabled,
   * by checking other dependent permissions
   * @param {string} pageId
   * @param {Permissions[]} permissions
   * @returns {boolean}
   */
  /**
   * Determines whether a role grants global AI access.
   * @param {Permissions[]} permissions The role permissions to check.
   * @returns {boolean} True when the role can use Agents.
   */
  private static hasAiAgentAccess(permissions: Permissions[]): boolean {
    const aiFeature = Feature.getFeatureStringByEnum(Features.AiAgentAccess)

    return !!permissions.find(p => p.feature === aiFeature)?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])
  }

  static isPagePermissionDisabled(pageId: string, permissions: Permissions[]): boolean {
    let permission

    switch (pageId) {
      case 'settings-workspace':
      case 'settings-teams':
      case 'settings-integrations':
      case 'settings-forms':
      case 'settings-connect': {
        const workspaceFeature = Feature.getFeatureStringByEnum(Features.WorkspaceManager)

        permission = permissions.find(p => p.feature === workspaceFeature)?.hasAnyAccessLevel([AccessLevels.Manage])

        return !permission
      }
      case 'ai-agents-chat':
      case 'settings-ai-library': {
        return !PermissionsService.hasAiAgentAccess(permissions)
      }
      case 'settings-ai-workspace-context':
      case 'settings-ai-models': {
        const workspaceFeature = Feature.getFeatureStringByEnum(Features.WorkspaceManager)

        permission = permissions.find(p => p.feature === workspaceFeature)?.hasAnyAccessLevel([AccessLevels.Manage])

        return !PermissionsService.hasAiAgentAccess(permissions) || !permission
      }
      case 'settings-absence': {
        const planningFeature = Feature.getFeatureStringByEnum(Features.UserPlanning)

        permission = permissions.find(p => p.feature === planningFeature)?.hasAnyAccessLevel([AccessLevels.Manage])

        return !permission
      }
      case 'users-all': {
        const userFeature = Feature.getFeatureStringByEnum(Features.UserMaster)

        permission = permissions
          .find(p => p.feature === userFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        return !permission
      }
      case 'companies-all': {
        const companyFeature = Feature.getFeatureStringByEnum(Features.CompanyMaster)

        permission = permissions
          .find(p => p.feature === companyFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        return !permission
      }
      case 'settings-categories': {
        const taskFeature = Feature.getFeatureStringByEnum(Features.TaskManage)
        const projectFeature = Feature.getFeatureStringByEnum(Features.ProjectManage)

        permission = permissions
          .find(p => p.feature === taskFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        const projectPermission = permissions
          .find(p => p.feature === projectFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        return !(permission || projectPermission)
      }
      case PagePermissionId.TimeTrackingControlling: {
        const projectTTFeature = Feature.getFeatureStringByEnum(Features.ProjectTimetracking)
        const userTTFeature = Feature.getFeatureStringByEnum(Features.UserTimetracking)

        permission = permissions
          .find(p => p.feature === projectTTFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        const userPermission = permissions
          .find(p => p.feature === userTTFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        return !(permission || userPermission)
      }
      case 'settings-custom-fields': {
        const projectManage = Feature.getFeatureStringByEnum(Features.ProjectManage)

        permission = permissions.find(p => p.feature === projectManage)?.hasAnyAccessLevel([AccessLevels.Manage])

        return !permission
      }
      case PagePermissionId.TeamPlanner: {
        const userPlanningFeature = Feature.getFeatureStringByEnum(Features.UserPlanning)

        permission = permissions
          .find(p => p.feature === userPlanningFeature)
          ?.hasAnyAccessLevel([AccessLevels.Manage, AccessLevels.Read])

        return !permission
      }
      default:
        return false
    }
  }

  /**
   * Check permissions for settings categories item
   * @param {boolean} isPageVisible
   * @param {UserPermissions} userPermissions
   * @returns {boolean}
   */
  private shouldHideCategories(isPageVisible: boolean, userPermissions: UserPermissions): boolean {
    const hasProjectTaskPermission = userPermissions?.permissions?.find(p =>
      ['task-manage-config', 'project-manage-config'].includes(p.feature)
    )

    return isPageVisible && !(userPermissions.isAdmin || hasProjectTaskPermission)
  }

  /**
   * Check permissions for settings categories item
   * @param {UserPermissions} userPermissions
   * @returns {boolean}
   */
  private hasUserPlannerPermissions(userPermissions: UserPermissions): boolean {
    const userPlanningFeature = Feature.getFeatureStringByEnum(Features.UserPlanning)
    const permission = userPermissions?.permissions?.find(p => p.feature === userPlanningFeature)

    return (
      permission?.accessLevels?.some(accessLevel => [AccessLevels.Read, AccessLevels.Manage].includes(accessLevel)) ??
      false
    )
  }

  /**
   * Sets the default query params for the users query
   * @param {QueryParams} [queryParams={}]
   * @returns {QueryParams}
   */
  private setDefaultQueryParams(queryParams: QueryParams = {}): QueryParams {
    return {
      ...queryParams,
      pageSize: queryParams.pageSize ?? 1000,
      page: queryParams.page ?? 1
    }
  }
}
